import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, UserPlus, KeyRound, Trash2, MoreHorizontal, Check, X, Clock } from 'lucide-react';
import { Spinner } from 'baseui/spinner';
import { Input } from 'baseui/input';
import { toaster } from 'baseui/toast';
import { TableContent } from '../LayoutPage';
import { useGKUsers, useCreateGKUser, useDeleteGKUser, useApproveGkUser, useRejectGkUser } from '../../hooks/useGKUsers';
import type { GKUser } from '../../api/gkApi';
import { FormattedPhoneInput } from '../../components/FormattedPhoneInput';
import { Country } from 'baseui/phone-input';
import { useFdCan } from '../../hooks/useFdCan';
import './gatekeeperUsers.css';

// ── Helpers ──

function initials(preferred_username: string): string {
  return preferred_username
    .trim()
    .split(/[\s_-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function statusBadgeClass(status: string): string {
  if (status === 'CONFIRMED') return 'gku-badge-confirmed';
  if (status === 'FORCE_CHANGE_PASSWORD') return 'gku-badge-pending';
  return 'gku-badge-other';
}

function statusDotClass(status: string): string {
  if (status === 'CONFIRMED') return 'gku-dot-confirmed';
  if (status === 'FORCE_CHANGE_PASSWORD') return 'gku-dot-pending';
  return 'gku-dot-other';
}

function fmtDate(d: string): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

// ── Detail pane (view mode) ──

interface DetailProps {
  user: GKUser;
  onDeleted: () => void;
  onApproved: () => void;
}

const UserDetail: React.FC<DetailProps> = ({ user, onDeleted, onApproved }) => {
  // employees is view-only for the tenant Front Desk (it never deletes): no actions menu, no approve/reject.
  const canManage = useFdCan('employees', 'edit');
  const canApprove = useFdCan('employees', 'approve');
  const deleteMutation = useDeleteGKUser();
  const approveMutation = useApproveGkUser();
  const rejectMutation = useRejectGkUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isPending = user.enabled === false;

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(user.username);
      toaster.positive('User deleted successfully', { autoHideDuration: 4000 });
      onDeleted();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.error || err?.message || 'Failed to delete user', { autoHideDuration: 5000 });
    }
  };

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync(user.username);
      toaster.positive('User approved successfully', { autoHideDuration: 4000 });
      onApproved();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.error || err?.message || 'Failed to approve user', { autoHideDuration: 5000 });
    }
  };

  const handleReject = async () => {
    try {
      await rejectMutation.mutateAsync(user.username);
      toaster.positive('User rejected successfully', { autoHideDuration: 4000 });
      onDeleted();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.error || err?.message || 'Failed to reject user', { autoHideDuration: 5000 });
    }
  };

  const av = initials(user.preferred_username || user.username) || '?';

  return (
    <div className="gku-detail-inner">
      {/* Header */}
      <div className="gku-detail-header">
        <div className="gku-detail-avatar">{av}</div>
        <div style={{ flex: 1 }}>
          <p className="gku-detail-title">{user.preferred_username || user.username}</p>
          <p className="gku-detail-sub">{user.phone_number}</p>
        </div>
        {canManage && (
        <div className="gku-menu-wrap" ref={menuRef}>
          <button
            className="gku-menu-btn"
            onClick={() => { setMenuOpen((o) => !o); setShowConfirm(false); }}
            aria-label="Actions"
          >
            <MoreHorizontal size={18} />
          </button>
          {menuOpen && (
            <div className="gku-menu-dropdown">
              {!showConfirm ? (
                <button
                  className="gku-menu-item gku-menu-item-danger"
                  onClick={() => setShowConfirm(true)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 size={14} />
                  Delete User
                </button>
              ) : (
                <div className="gku-menu-confirm">
                  <p className="gku-menu-confirm-text">This cannot be undone.</p>
                  <div className="gku-menu-confirm-actions">
                    <button className="gku-btn-secondary gku-btn-sm" onClick={() => setShowConfirm(false)} disabled={deleteMutation.isPending}>
                      Cancel
                    </button>
                    <button className="gku-btn-danger gku-btn-sm" onClick={handleDelete} disabled={deleteMutation.isPending}>
                      {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {isPending && (
        <div className="gku-section">
          <div className="gku-pending-banner">
            <span className="gku-status-badge gku-badge-pending-approval">
              <Clock size={12} />
              Pending approval
            </span>
            {!canApprove ? null : !showRejectConfirm ? (
              <div className="gku-pending-banner-actions">
                <button
                  className="gku-btn-approve gku-btn-sm"
                  onClick={handleApprove}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                >
                  <Check size={14} />
                  {approveMutation.isPending ? 'Approving…' : 'Approve'}
                </button>
                <button
                  className="gku-btn-danger gku-btn-sm"
                  onClick={() => setShowRejectConfirm(true)}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                >
                  <X size={14} />
                  Reject
                </button>
              </div>
            ) : (
              <div className="gku-pending-banner-actions">
                <p className="gku-menu-confirm-text" style={{ margin: 0, marginRight: 8 }}>Reject this user? This cannot be undone.</p>
                <button className="gku-btn-secondary gku-btn-sm" onClick={() => setShowRejectConfirm(false)} disabled={rejectMutation.isPending}>
                  Cancel
                </button>
                <button className="gku-btn-danger gku-btn-sm" onClick={handleReject} disabled={rejectMutation.isPending}>
                  {rejectMutation.isPending ? 'Rejecting…' : 'Confirm'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fields — 2-column grid */}
      <div className="gku-section">
        <div className="gku-section-label">User Information</div>
        <div className="gku-fields-grid">
          <div className="gku-field">
            <span className="gku-field-label">Employee Code</span>
            <span className="gku-field-value">{user.preferred_username || '—'}</span>
          </div>
          <div className="gku-field">
            <span className="gku-field-label">Status</span>
            <span className="gku-field-value">
              <span className={`gku-status-badge ${statusBadgeClass(user.status)}`}>{user.status || 'UNKNOWN'}</span>
            </span>
          </div>
          <div className="gku-field">
            <span className="gku-field-label">Phone Number</span>
            <span className="gku-field-value">{user.phone_number || '—'}</span>
          </div>
          <div className="gku-field">
            <span className="gku-field-label">Created</span>
            <span className="gku-field-value">{fmtDate(user.created)}</span>
          </div>
          <div className="gku-field" style={{ gridColumn: '1 / -1' }}>
            <span className="gku-field-label">Username (Cognito)</span>
            <span className="gku-field-value" style={{ color: '#9ca3af', fontSize: '13px' }}>{user.username}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Create form ──

interface CreateFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

const CreateForm: React.FC<CreateFormProps> = ({ onCreated, onCancel }) => {
  const createMutation = useCreateGKUser();
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState<Country>({ label: 'Dominican Republic', id: 'DO', dialCode: '+1' } as Country);

  const isValid = name.trim() !== '' && phoneNumber.trim() !== '' && password.length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({ preferred_username: name.trim(), phone_number: phoneNumber.trim(), password });
      toaster.positive('Gatekeeper user created successfully', { autoHideDuration: 4000 });
      onCreated();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.error || err?.message || 'Failed to create user', { autoHideDuration: 5000 });
    }
  };

  return (
    <div className="gku-detail-inner">
      <p className="gku-form-title">New Gatekeeper User</p>
      <p className="gku-form-sub">Create a new account for the Gatekeeper mobile app.</p>

      <form onSubmit={handleSubmit}>
        <div className="gku-field-group">
          <label className="gku-label">Employee Code / Preferred Username</label>
          <input
            className="gku-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. EMP-001 or John Doe"
            autoFocus
          />
        </div>

        <div className="gku-field-group">
          <label className="gku-label">Phone Number (used as username)</label>
          <FormattedPhoneInput
            value={phoneNumber}
            country={country}
            onChange={(value, selectedCountry) => { setPhoneNumber(value); setCountry(selectedCountry); }}
            placeholder="Phone number"
            showFormatHint={false}
          />
        </div>

        <div className="gku-field-group">
          <label className="gku-label">Password (min 8 characters)</label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
            placeholder="Set a password"
          />
        </div>

        <div className="gku-actions">
          <button type="button" className="gku-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="gku-btn-primary" disabled={!isValid || createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create User'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Pending approval row (list section) ──

interface PendingRowProps {
  user: GKUser;
  onChanged: () => void;
}

const PendingRow: React.FC<PendingRowProps> = ({ user, onChanged }) => {
  const canApprove = useFdCan('employees', 'approve');
  const approveMutation = useApproveGkUser();
  const rejectMutation = useRejectGkUser();
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const av = initials(user.preferred_username || user.username) || '?';
  const busy = approveMutation.isPending || rejectMutation.isPending;

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await approveMutation.mutateAsync(user.username);
      toaster.positive('User approved successfully', { autoHideDuration: 4000 });
      onChanged();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.error || err?.message || 'Failed to approve user', { autoHideDuration: 5000 });
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await rejectMutation.mutateAsync(user.username);
      toaster.positive('User rejected successfully', { autoHideDuration: 4000 });
      onChanged();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.error || err?.message || 'Failed to reject user', { autoHideDuration: 5000 });
    }
  };

  return (
    <div className="gku-pending-item">
      <div className="gku-avatar">{av}</div>
      <div className="gku-item-body">
        <div className="gku-item-name">{user.preferred_username || user.username}</div>
        <div className="gku-item-phone">{user.phone_number}</div>
        <div className="gku-pending-item-date">Requested {fmtDate(user.created)}</div>
      </div>
      {!canApprove ? null : !showRejectConfirm ? (
        <div className="gku-pending-item-actions">
          <button
            className="gku-icon-btn gku-icon-btn-approve"
            onClick={handleApprove}
            disabled={busy}
            aria-label="Approve"
            title="Approve"
          >
            {approveMutation.isPending ? <Spinner $size={14} /> : <Check size={16} />}
          </button>
          <button
            className="gku-icon-btn gku-icon-btn-reject"
            onClick={(e) => { e.stopPropagation(); setShowRejectConfirm(true); }}
            disabled={busy}
            aria-label="Reject"
            title="Reject"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="gku-pending-item-confirm" onClick={(e) => e.stopPropagation()}>
          <span className="gku-menu-confirm-text" style={{ margin: 0 }}>Reject?</span>
          <button className="gku-btn-secondary gku-btn-sm" onClick={() => setShowRejectConfirm(false)} disabled={busy}>
            Cancel
          </button>
          <button className="gku-btn-danger gku-btn-sm" onClick={handleReject} disabled={busy}>
            {rejectMutation.isPending ? 'Rejecting…' : 'Confirm'}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main page ──

type PanelMode = 'none' | 'view' | 'create';

export const GatekeeperUsers: React.FC = () => {
  const canCreate = useFdCan('employees', 'create');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<GKUser | null>(null);
  const [panelMode, setPanelMode] = useState<PanelMode>('none');

  const { data, isLoading, refetch } = useGKUsers();
  const users = useMemo(() => data?.users ?? [], [data]);

  const pendingUsers = useMemo(() => users.filter((u) => u.enabled === false), [users]);
  const activeUsers = useMemo(() => users.filter((u) => u.enabled !== false), [users]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return activeUsers;
    return activeUsers.filter(
      (u) =>
        (u.preferred_username || '').toLowerCase().includes(q) ||
        u.phone_number.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q)
    );
  }, [activeUsers, searchTerm]);

  const openCreate = () => {
    setSelectedUser(null);
    setPanelMode('create');
  };

  const openView = (user: GKUser) => {
    setSelectedUser(user);
    setPanelMode('view');
  };

  const closePanel = () => {
    setSelectedUser(null);
    setPanelMode('none');
  };

  const hasSelection = panelMode !== 'none';

  return (
    <TableContent>
      <div className={`gku-app ${hasSelection ? 'has-selection' : ''}`}>
        {/* ── Left list pane ── */}
        <div className="gku-list-pane">
          <div className="gku-list-header">
            <div className="gku-list-title-row">
              <h2 className="gku-list-title">Gatekeeper Users</h2>
              {canCreate && (
                <button className="gku-new-btn" onClick={openCreate}>
                  <UserPlus size={14} />
                  New User
                </button>
              )}
            </div>
            <div className="gku-search-bar">
              <Search size={14} color="#9ca3af" />
              <input
                placeholder="Search by name or phone…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {pendingUsers.length > 0 && (
            <div className="gku-pending-section">
              <div className="gku-pending-section-header">
                <span>Pending approval</span>
                <span className="gku-pending-count-badge">{pendingUsers.length}</span>
              </div>
              <div className="gku-pending-list">
                {pendingUsers.map((user) => (
                  <PendingRow key={user.username} user={user} onChanged={() => refetch()} />
                ))}
              </div>
            </div>
          )}

          <div className="gku-count">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</div>

          <div className="gku-list-scroll">
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner $size={32} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '40px 16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                {searchTerm ? 'No users match your search.' : 'No gatekeeper users yet.'}
              </div>
            ) : (
              filtered.map((user) => {
                const av = initials(user.preferred_username || user.username) || '?';
                const isSelected = panelMode === 'view' && selectedUser?.username === user.username;
                return (
                  <div
                    key={user.username}
                    className={`gku-item ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => openView(user)}
                  >
                    <div className="gku-avatar">{av}</div>
                    <div className="gku-item-body">
                      <div className="gku-item-name">{user.preferred_username || user.username}</div>
                      <div className="gku-item-phone">{user.phone_number}</div>
                    </div>
                    <div className={`gku-status-dot ${statusDotClass(user.status)}`} title={user.status} />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right detail pane ── */}
        <div className="gku-detail-pane">
          {panelMode === 'none' && (
            <div className="gku-detail-empty">
              <KeyRound size={40} color="#d1d5db" />
              <p>Select a user to view details, or create a new one.</p>
            </div>
          )}

          {panelMode === 'view' && selectedUser && (
            <UserDetail
              key={selectedUser.username}
              user={selectedUser}
              onDeleted={() => { closePanel(); refetch(); }}
              onApproved={() => { closePanel(); refetch(); }}
            />
          )}

          {panelMode === 'create' && (
            <CreateForm
              onCreated={() => { closePanel(); refetch(); }}
              onCancel={closePanel}
            />
          )}
        </div>
      </div>
    </TableContent>
  );
};
